/**
 * "How long may I still wait here for free?" — counted down live, at the
 * pickup, from the time the CUSTOMER was given.
 *
 * The API sends an absolute window (`free_waiting.starts_at` / `ends_at`)
 * that already has the PARTNER's minutes, the airport-or-not rule and any
 * credited flight delay baked in; this component only ticks a clock against
 * `ends_at`. So the driver who pulls up twenty minutes early sees the whole
 * allowance still ahead of them plus the twenty minutes they gained, and is
 * told plainly that none of it is running yet.
 *
 * IT IS NOT A SECOND OVERTIME CLOCK. It counts DOWN to the moment the
 * overtime timer should start, and once that moment passes it stops
 * counting and becomes a sentence: what happened, and what to do. The
 * running-overtime clock above it (WaitingPanel) is the only thing still
 * ticking from then on, so the two can never show different elapsed times
 * for the same wait.
 *
 * What to do is the portal's own existing answers — start the overtime
 * timer (the button right below), message the office on WhatsApp (never
 * call), and report a no-show only once the passenger truly cannot be
 * reached.
 */
import React from 'react';
import { formatTime } from '../../../Utils/timezone';
import OfficeWhatsAppButton from './OfficeWhatsAppButton';
import {
  FREE_WAITING_COPY,
  formatElapsed,
  freeWaiting,
  freeWaitingDelayCapped,
  freeWaitingPhase,
  freeWaitingRemaining,
  freeWaitingTerms,
  flightDelayMinutes,
  freeWaitingHasDelay,
} from '../portalFormat';

export default function FreeWaitingBlock({
  trip, now = Date.now(), overtimeRunning = false, driverName = '', copy = FREE_WAITING_COPY,
}) {
  const window = freeWaiting(trip);
  const phase = freeWaitingPhase(trip, now);
  if (!window || !phase) return null;

  const capped = freeWaitingDelayCapped(trip);
  const expired = phase === 'expired';
  const terms = freeWaitingTerms(trip, copy);
  // The clock was moved by a flight, and by an amount we absorbed in full.
  const delayed = !expired && !capped && freeWaitingHasDelay(trip);

  return (
    <div
      className={`pp-free${expired ? ' pp-free--over' : ''}`}
      data-testid="portal-free-waiting"
      data-phase={phase}
    >
      <div className="pp-free__row">
        <span className="pp-free__label">
          {expired ? copy.label : copy.remaining}
        </span>
        {expired ? (
          <span className="pp-free__allowance">
            {copy.allowance(window.minutes, window.airport)}
          </span>
        ) : (
          <span
            className="pp-free__clock"
            role="timer"
            aria-label={copy.remaining}
            data-testid="portal-free-waiting-clock"
          >
            {formatElapsed(freeWaitingRemaining(trip, now))}
          </span>
        )}
      </div>

      {phase === 'early' ? (
        <p className="pp-free__note">{copy.early(formatTime(window.starts_at))}</p>
      ) : null}

      {phase === 'running' && !delayed ? (
        <p className="pp-free__note">
          {`${copy.allowance(window.minutes, window.airport)} · ${copy.until(formatTime(window.ends_at))}`}
        </p>
      ) : null}

      {/* Where the clock was moved, the delay REPLACES the plain "until"
          line rather than sitting next to it: one sentence carrying both
          the reason and the time, so a driver never wonders why the free
          time reaches past the pickup they were given. Suppressed once it
          is capped — that says it already, and says something the driver
          has to act on besides. */}
      {delayed ? (
        <p className="pp-free__note">
          {copy.delayed(window.delay_credited_minutes, formatTime(window.ends_at))}
        </p>
      ) : null}

      {capped ? (
        <p className="pp-free__alert" role="status">
          {copy.capped(
            flightDelayMinutes(trip) || window.flight_delay_minutes,
            window.max_flight_delay_minutes,
          )}
          {' '}
          {copy.cappedWhatNow}
        </p>
      ) : null}

      {expired ? (
        <p className="pp-free__alert" role="status">
          {copy.expired(formatTime(window.ends_at))}
          {/* Once the timer is running the driver has already done the first
              thing asked of them; repeating "start the overtime" would be
              noise on the one screen that must stay unambiguous. */}
          {overtimeRunning ? null : ` ${copy.expiredWhatNow}`}
        </p>
      ) : null}

      {/*
        The chat opens already saying which trip and which of the two
        things happened — a capped flight is not the same conversation as
        an allowance that simply ran out, and the office should not have to
        ask which one it is.
      */}
      {expired || capped ? (
        <OfficeWhatsAppButton
          message={capped
            ? copy.officeMessageCapped(trip, driverName)
            : copy.officeMessage(trip, driverName)}
        />
      ) : null}

      {/* Last, and quiet. The driver reads this to understand the number
          above it, not to be told to do anything — so it sits below the
          lines that DO ask something of them, and never borrows their
          colour. The live line is marked from the window's own `airport`
          flag, so it cannot disagree with the clock. */}
      {terms ? (
        <div className="pp-free__terms" data-testid="portal-free-waiting-terms">
          <span className="pp-free__terms-title">{terms.title}</span>
          <ul className="pp-free__terms-list">
            {terms.rows.map((row) => (
              <li
                key={row.key}
                className={`pp-free__term${row.active ? ' pp-free__term--live' : ''}`}
                data-testid={`portal-free-waiting-term-${row.key}`}
                data-live={row.active ? 'true' : 'false'}
              >
                {row.text}
              </li>
            ))}
          </ul>
          {terms.note ? <p className="pp-free__terms-note">{terms.note}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
